// Package imports:
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { NumberFormatValues } from 'react-number-format';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight, faInfoCircle, faRotateRight } from '@fortawesome/pro-solid-svg-icons';
// Component imports:
import Loading from '../../../ui-elements/Loading/Loading';
import DisplayBox from '../../DisplayBox/DisplayBox';
import CompanyValuationTable from './CompanyValuationTable';
import Alert from '../../../ui-elements/Alert/Alert';
import Button from '../../../ui-elements/Button/Button';
import Input from '../../../ui-elements/Input/Input';
import CompanyNavBar from '../CompanyNavBar/CompanyNavBar';
import HeadSection from '../../../ui-elements/HeadSection/HeadSection';
import CompanyTitle from '../CompanyTitle/CompanyTitle';
import Tooltip from '../../../ui-elements/Tooltip/Tooltip';
import Link from '../../../ui-elements/Link/Link';
import Table from '../../../ui-elements/Table/Table';
// Service imports:
import { useFetchedApiLmdData } from '../../../services/apiHooks';
import { formatNumber, showValuation } from '../../../services/utils';
// Type imports:
import { ICompanyViewModel, ICompanyValuation } from '../../../types/CompanyTypes';
import { IDefaultProps } from '../../../types/Types';
// Context imports:
import { AccessTokenContext } from '../../../contexts/AccessTokenContext';

const MIN_WACC = 0.1;
const MAX_WACC = 20;
const MIN_GROWTH = 0.1;
const MAX_GROWTH = 10;

const Valuation: React.FC<ICompanyViewModel> = ({
    id,
    isAuthenticated,
    isSubscriber
}) => {
    const accessToken = useContext(AccessTokenContext);
    const [ defaultProps, setDefaultProps ] = useState<IDefaultProps>({
        accessToken,
        refreshRateMs: 60*1000
    });

    useEffect(() => {
        setDefaultProps({
            ...defaultProps,
            accessToken
        });
    }, [accessToken]);

    const [wacc, setWacc] = useState<number | null>(null);
    const [growth, setGrowth] = useState<number | null>(null);
    const [usingUserWacc, setUsingUserWacc] = useState<boolean | null>(null);
    const [usingUserGrowth, setUsingUserGrowth] = useState<boolean | null>(null);
    const usingUserInput = useMemo(() => usingUserWacc || usingUserGrowth, [usingUserWacc, usingUserGrowth]);

    const [invalidWacc, setInvalidWacc] = useState(false);
    const [invalidGrowth, setInvalidGrowth] = useState(false);

    const [params, setParams] = useState<string | null>(null);

    const [formulaTablesOpen, setFormulaTablesOpen] = useState(false);
    const [finalTableOpen, setFinalTableOpen] = useState(false);

    const [initialWacc, setInitialWacc] = useState<number | null>(null);
    const [initialGrowth, setInitialGrowth] = useState<number | null>(null);
    
    const [waccChanged, setWaccChanged] = useState(false);
    const [growthChanged, setGrowthChanged] = useState(false);

    useEffect(() => {
        if (wacc !== null && growth !== null) {
            setInvalidGrowth(false);
            setInvalidWacc(wacc <= growth);
        }

        if (usingUserWacc !== null) {
            const waccChangeTimer = setTimeout(() => {
                setWaccChanged(true);

                const waccTimer = setTimeout(() => {
                    setWaccChanged(false);
                }, 2000);

                return () => clearTimeout(waccTimer);
            }, 200);
            return () => clearTimeout(waccChangeTimer);
        }
    }, [wacc]);
    useEffect(() => {
        if (wacc !== null && growth !== null) {
            setInvalidWacc(false);
            setInvalidGrowth(growth >= wacc);
        }

        if (usingUserGrowth !== null) {
            const growthChangeTimer = setTimeout(() => {
                setGrowthChanged(true);

                const growthTimer = setTimeout(() => {
                    setGrowthChanged(false);
                }, 2000);

                return () => clearTimeout(growthTimer);
            }, 200);
            return () => clearTimeout(growthChangeTimer);
        }
    }, [growth]);

    const timeoutFunctionIdRef = useRef<number | null>(null);
    // Helper delay function.
    const withDelay = (func: () => void) => {
        const timeoutFunctionId = timeoutFunctionIdRef.current;
        if (timeoutFunctionId) window.clearTimeout(timeoutFunctionId);

        timeoutFunctionIdRef.current = window.setTimeout(func, 400);
    }

    useEffect(() => {
        if (!usingUserInput) {
            if (params || !data || data instanceof Error) {
                setParams('');
            }
        }
    }, [usingUserInput]);
    useEffect(() => {
        if (wacc !== null && growth !== null && wacc <= growth) return;
        const p = usingUserInput
            ? (usingUserWacc && wacc
                ? usingUserGrowth && growth
                ? `?wacc=${wacc/100}&growth=${growth/100}`
                : `?wacc=${wacc/100}`
                : usingUserGrowth && growth
                ? `?growth=${growth/100}`
                : ''
            )
            : '';
        withDelay(() => setParams(p));
    }, [wacc, growth, usingUserWacc, usingUserGrowth]);
    
    const data = useFetchedApiLmdData<ICompanyValuation>(
        `/v1/market_data/v1/company_valuation/${id}/valuation/v2${params}`,
        accessToken
    );

    const shouldShowValuation = useMemo(() => {
        if (data instanceof Error) return 'error';
        if (data === null) return 'loading';
        if (usingUserInput) return 'show';
        return showValuation(data);
    }, [data])

    useEffect(() => {
        if (data === null || data instanceof Error) return;
        if (data.wacc !== null) {
            setWacc(data.wacc*100);
            if (initialWacc === null) setInitialWacc(data.wacc*100);
        }
        if (data.gdp_growth_rate !== null) {
            setGrowth(data.gdp_growth_rate*100);
            if (initialGrowth === null) setInitialGrowth(data.gdp_growth_rate*100);
        }
    }, [data]);

    useEffect(() => {
        if (!usingUserWacc) setWacc(initialWacc);
        if (!usingUserGrowth) setGrowth(initialGrowth);
    }, [usingUserWacc, usingUserGrowth])

    const displayBody = () => {
        return data === null
        ? <Loading />
        : data instanceof Error
        ? <Alert
            type='alert'
            headText='Ekki tókst að verðmeta fyrirtæki'
            text={<>
                <p>Keldan styðst við ýmsar forsendur við gerð verðmatsins.<br />Eftirfarandi ástæður gætu valdið því að ekki tekst að verðmeta fyrirtæki:</p>
                <ul className='blue-dot-list'>
                    <li>Ársreikningar eru of gamlir eða of fáir.</li>
                    <li>Gögn vantar við útreikning ávöxtunarkröfu.</li>
                    <li>Gögn vantar við útreikning frjáls sjóðstreymis.</li>
                    <li>Atvinnugrein fyrirtækis bendir til þess að verðmatsaðferð eigi ekki við.</li>
                    <li>Niðurstöður verðmats eru utan þess sem eðlilegt gæti talist.</li>
                    <li>Ársreikningar eru í öðrum gjaldmiðli en ISK.</li>
                </ul>
            </>}
        />
        : shouldShowValuation === 'negative-fcf'
        ? <div className='grid-items'>
            <div className='grid-item'>
                <Alert
                    type='info'
                    headText='Fyrirtæki er með neikvætt frjálst sjóðstreymi'
                    text='Ekki er unnt að birta verðmat fyrir fyrirtæki með neikvætt frjálst sjóðstreymi en sjá má útreikninga hér fyrir neðan.'
                />
            </div>
            <div className='grid-item'>
                <DisplayBox
                    displayBoxSize='lg'
                    className='valuation-calculations'
                >
                    <Table
                        data={[{
                            name: 'EBIT',
                            value: data.ebit
                        }, {
                            name: 'Skattar',
                            value: data.taxes
                        }, {
                            name: 'Afskriftir',
                            value: data.depreciation !== null ? -1*data.depreciation : null
                        }, {
                            name: 'Fjárfesting',
                            value: data.investment
                        }, {
                            name: 'Breyting veltufjármuna',
                            value: data.change_in_nwc
                        }]}
                        columns={[{
                            title: 'Skýring',
                            dataAccessor: ({name}) => name,
                            textAlign: 'left'
                        }, {
                            title: 'Upphæð',
                            dataAccessor: ({value}) => formatNumber(value, '-', 0, ' kr.'),
                            textAlign: 'right'
                        }]}
                        tableSize='lg'
                    />
                    <div className='final-value-row'>
                        <span className='text'>Samtals frjálst sjóðstreymi</span>
                        <span className='value'>{formatNumber(data.free_cashflow, '-', 0, ' kr.')}</span>
                    </div>
                </DisplayBox>
            </div>
        </div>
        : shouldShowValuation !== 'show'
        ? <Alert
            type='alert'
            headText='Ekki tókst að verðmeta fyrirtæki'
            text={<>
                <p>Keldan styðst við ýmsar forsendur við gerð verðmatsins.<br />Eftirfarandi ástæður gætu valdið því að ekki tekst að verðmeta fyrirtæki:</p>
                <ul className='blue-dot-list'>
                    <li>Ársreikningar eru of gamlir eða of fáir.</li>
                    <li>Gögn vantar við útreikning ávöxtunarkröfu.</li>
                    <li>Gögn vantar við útreikning frjáls sjóðstreymis.</li>
                    <li>Atvinnugrein fyrirtækis bendir til þess að verðmatsaðferð eigi ekki við.</li>
                    <li>Niðurstöður verðmats eru utan þess sem eðlilegt gæti talist.</li>
                    <li>Ársreikningar eru í öðrum gjaldmiðli en ISK.</li>
                </ul>
            </>}
        />
        : <div className='grid-items'>
            <div className='grid-item'>
                <DisplayBox
                    displayBoxSize='lg'
                    className='summary-box'
                >
                    <div className='description'>
                        <h3>Niðurstöður verðmats fyrir <span className='bold-name'>{data.company_name}</span></h3>
                        <p>Hér koma niðurstöður verðmatsins sem byggðar eru á frjálsu sjóðstreymi.</p>
                        <p className='bold-text'>Frjálst sjóðstreymi er reiknað út frá nýjasta ársreikningi og geta þeir útreikningar því verið rangir eða gefið villandi mynd af fyrirtækinu, skoðið því með það í huga.</p>
                    </div>
                    <div className='grey-box'>
                        <div className={cx('value', { 'highlighted': waccChanged || growthChanged })}>
                            <span className='value-number'>{formatNumber(data.equity_valuation, '-', 0)}</span>
                            <span className='value-unit'>kr.</span>
                        </div>
                        <div className='title'>Samtals virði eigin fjár</div>
                    </div>
                </DisplayBox>
            </div>
            <div className='grid-item'>
                <DisplayBox
                    displayBoxSize='lg'
                    className='model-box'
                >
                    <h3>Verðmatsmódel</h3>
                    <p className='italic-text'>Í þessu einfaldaða verðmatsmódeli er hægt að breyta gildum fyrir WACC og vöxt fyrirtækisins með rennistikunum hér að neðan. Aðrar breytur módelsins eru fastar.</p>
                    <div className='variables'>
                        <div className='variables--section'>
                            <h6>
                                Veginn fjármagnskostnaður (WACC)
                                <Tooltip
                                    text={<FontAwesomeIcon icon={faInfoCircle} className='icon-fa' />}
                                    tooltip={<p>
                                        Fyrirtæki fjármagna fjárfestingar með eigin fé eða lánum en kostnaður þessara fjármögnunarleiða
                                        er mismunandi. WACC (Weighted Average Cost of Capital) tekur saman eiginfjárkröfu og skuldakröfu
                                        félags í réttum hlutföllum. WACC er svo notað til þess að núvirða framtíðar greiðsluflæði félags.
                                    </p>}
                                    noUnderline
                                    size='lg'
                                />
                            </h6>
                            <p className='variables-description'>Meðal kostnaður fjármagnsins sem fyrirtækið notar til að standa undir rekstri.</p>
                            <div className='slider-wrapper'>
                                <div className='slider-box'>
                                    <input
                                        type='range'
                                        min='0'
                                        max='20'
                                        value={wacc ?? 0}
                                        step='0.01'
                                        onChange={(e) => {
                                            setWacc(parseFloat(e.currentTarget.value));
                                        }}
                                        style={{
                                            background: `linear-gradient(to right, #1D3097 0%, #1D3097 ${wacc ? wacc/0.2 : 0}%, #D4DEFF ${wacc ? wacc/0.2 : 0}%, #D4DEFF 100%)`
                                        }}
                                        onInput={() => setUsingUserWacc(true)}
                                    />
                                    <div className="end-values">
                                        <span className="low-value">0%</span>
                                        <span className="high-value">20%</span>
                                    </div>
                                </div>
                                <div className='input-and-button'>
                                    <div className='slider-input'>
                                        <Input
                                            className='slider-value'
                                            inputType='numberFormat'
                                            inputSize='lg'
                                            textAlign='left'
                                            autoComplete='off'
                                            value={wacc ?? undefined}
                                            thousandSeparator='.'
                                            decimalSeparator=','
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowLeadingZeros={false}
                                            onValueChange={(values: NumberFormatValues) => {
                                                if (values.floatValue) {
                                                    setWacc(values.floatValue);
                                                }
                                            }}
                                            onInput={() => setUsingUserWacc(true)}
                                            allowNegative={false}
                                            isAllowed={(values) => values.floatValue !== undefined && values.floatValue >= MIN_WACC && values.floatValue <= MAX_WACC }
                                        />
                                        <span className='percent-symbol'>%</span>
                                    </div>
                                    <Button
                                        size='lg'
                                        buttonType='secondary'
                                        icon={faRotateRight}
                                        onClick={() => {
                                            setUsingUserWacc(false);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={cx('invalid-params-message', {'show': invalidWacc})}>
                                WACC getur ekki verið lægri en vöxtur
                            </div>
                        </div>
                        <div className='variables--section'>
                            <h6>
                                Vöxtur
                                <Tooltip
                                    text={<FontAwesomeIcon icon={faInfoCircle} className='icon-fa' />}
                                    tooltip={<p>
                                        Vöxtur, eilífðarvöxtur eða lokavöxtur er sá vöxtur sem áætlaður er á hagnað eða peningaflæði
                                        fyrirtækis út í eilífðina. Við áætlun vaxtar þarf að finna jafnvægi milli skammtíma- og
                                        langtímavaxtar því verðmatsmódelið er afar næmt á breytingar á vextinum.
                                    </p>}
                                    noUnderline
                                    size='lg'
                                />
                            </h6>
                            <p className='variables-description'>Meðal hagvöxtur á Íslandi síðan 1995.</p>
                            <div className='slider-wrapper'>
                                <div className='slider-box'>
                                    <input
                                        type='range'
                                        min='0'
                                        max='10'
                                        value={growth ?? 0}
                                        step='0.01'
                                        onChange={(e) => {
                                            setGrowth(parseFloat(e.currentTarget.value));
                                        }}
                                        style={{
                                            background: `linear-gradient(to right, #1D3097 0%, #1D3097 ${growth ? growth/0.1 : 0}%, #D4DEFF ${growth ? growth/0.1 : 0}%, #D4DEFF 100%)`
                                        }}
                                        onInput={() => setUsingUserGrowth(true)}
                                    />
                                    <div className="end-values">
                                        <span className="low-value">0%</span>
                                        <span className="high-value">10%</span>
                                    </div>
                                </div>
                                <div className='input-and-button'>
                                    <div className='slider-input'>
                                        <Input
                                            className='slider-value'
                                            inputType='numberFormat'
                                            inputSize='lg'
                                            textAlign='left'
                                            autoComplete='off'
                                            value={growth ?? undefined}
                                            thousandSeparator='.'
                                            decimalSeparator=','
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            allowLeadingZeros={false}
                                            onValueChange={(values: NumberFormatValues) => {
                                                if (values.floatValue) {
                                                    setGrowth(values.floatValue);
                                                }
                                            }}
                                            onInput={() => setUsingUserGrowth(true)}
                                            allowNegative={false}
                                            isAllowed={(values) => values.floatValue !== undefined && values.floatValue >= MIN_GROWTH && values.floatValue <= MAX_GROWTH }
                                        />
                                        <span className='percent-symbol'>%</span>
                                    </div>
                                    <Button
                                        size='lg'
                                        buttonType='secondary'
                                        icon={faRotateRight}
                                        onClick={() => {
                                            setUsingUserGrowth(false);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={cx('invalid-params-message', {'show': invalidGrowth})}>
                                Vöxtur getur ekki verið hærri en WACC.
                            </div>
                        </div>
                    </div>
                    <div className='calculations'>
                        <div className='calculations--line'>
                            <CompanyValuationTable
                                data={data}
                                type='return-on-equity'
                                tableOpen={formulaTablesOpen}
                                setTableOpen={setFormulaTablesOpen}
                            />
                            <CompanyValuationTable
                                data={data}
                                type='return-on-debt'
                                tableOpen={formulaTablesOpen}
                                setTableOpen={setFormulaTablesOpen}
                            />
                            <CompanyValuationTable
                                data={data}
                                type='wacc'
                                tableOpen={formulaTablesOpen}
                                setTableOpen={setFormulaTablesOpen}
                                waccHighlight={waccChanged}
                                growthHighlight={growthChanged}
                            />
                        </div>
                        <div className='calculations--line'>
                            <CompanyValuationTable
                                data={data}
                                type='fcf'
                                tableOpen={formulaTablesOpen}
                                setTableOpen={setFormulaTablesOpen}
                            />
                            <CompanyValuationTable
                                data={data}
                                type='valuation'
                                tableOpen={formulaTablesOpen}
                                setTableOpen={setFormulaTablesOpen}
                                waccHighlight={waccChanged}
                                growthHighlight={growthChanged}
                            />
                        </div>
                    </div>
                </DisplayBox>
            </div>
            <div className='grid-item'>
                <DisplayBox
                    displayBoxSize='lg'
                    className='summary-box final-result-box'
                >
                    <div className='description'>
                        <h3>Virði eigin fjár hjá <span className='bold-name'>{data.company_name}</span></h3>
                        <p>Lokaútreikningur á virði eigin fjár er reiknaður á eftirfarandi hátt eins og lýst er hér við niðurstöðu.</p>
                        {window.matchMedia('(min-width: 1024px)').matches &&
                            <div className={cx('details', { 'show': finalTableOpen })}>
                                <p>Verðmatið byggir á núvirtu áætluðu sjóðstreymi til eilífðar og er á nafngrunni, í íslenskum krónum. Stuðst er við reiknuð gildi frá Dr. Aswath Damodaran við útreikning ávöxtunarkröfu. Athugið að engu sérstöku álagi hefur verið bætt við ávöxtunarkröfu vegna seljanleika, rekstraróvissu eða annarra þátta. Ekki er tekið tillit til erlendra gjaldmiðla við útreikning ávöxtunarkrafna og WACC.</p>
                                <p>Virði fyrirtækisins má lýsa með eftirfarandi jöfnu:</p>
                                <img src="https://cdn.livemarketdata.com/KeldanImages/CompanyValuation.png" alt="Virði fyrirtækis" width="640" height="115" />
                            </div>
                        }
                    </div>
                    <div className='results'>
                        <CompanyValuationTable
                            data={data}
                            type='equity-valuation'
                            tableOpen={finalTableOpen}
                            setTableOpen={setFinalTableOpen}
                            waccHighlight={waccChanged}
                            growthHighlight={growthChanged}
                        />
                    </div>
                    {window.matchMedia('(max-width: 1023px)').matches &&
                        <div className={cx('description details', { 'show': finalTableOpen })}>
                            <p>Verðmatið byggir á núvirtu áætluðu sjóðstreymi til eilífðar og er á nafngrunni, í íslenskum krónum. Stuðst er við reiknuð gildi frá Dr. Aswath Damodaran við útreikning ávöxtunarkröfu. Athugið að engu sérstöku álagi hefur verið bætt við ávöxtunarkröfu vegna seljanleika, rekstraróvissu eða annarra þátta. Ekki er tekið tillit til erlendra gjaldmiðla við útreikning ávöxtunarkrafna og WACC.</p>
                            <p>Virði fyrirtækisins má lýsa með eftirfarandi jöfnu:</p>
                            {window.matchMedia('(max-width: 767px)').matches
                                ? <img src="https://cdn.livemarketdata.com/KeldanImages/CompanyValuationMobile.png" alt="Virði fyrirtækis" width="345" height="217" />
                                : <img src="https://cdn.livemarketdata.com/KeldanImages/CompanyValuation.png" alt="Virði fyrirtækis" width="640" height="115" />
                            }
                        </div>
                    }
                </DisplayBox>
            </div>
        </div>
    }

    return (
        <div className='main KCL_company-subPage valuation-page'>
            <div className='main__inner-fluid'>
                <CompanyNavBar isAuthenticated={isAuthenticated} isSubscriber={isSubscriber} id={id} />
                <div className='head-wrapper'>
                    <div className='shell'>
                        <HeadSection
                            title=''
                            description={<>
                                <h1>Verðmat Keldunnar</h1>
                                <p>Verðmat Keldunnar byggir á núvirtu áætluðu sjóðstreymi til eilífðar í íslenskum krónum.</p>
                            </>}
                            actions={<Link url='/Markadir/Fjarmalahugtok' linkSize='16'>
                                <FontAwesomeIcon icon={faAngleRight} /> Nánar um verðmat í Fjármálahugtökum
                            </Link>}
                            rightSide={
                                <Alert
                                    type='info'
                                    headText='Fyrirvari'
                                    text={<p>
                                        Verðmatið er ekki fjárfestingarráðgjöf og skal ekki notað til ákvörðunartöku auk þess sem upplýsingar í verðmatinu kunna að vera rangar.
                                        Þetta verðmat er gert til fróðleiks, samanburðar og skemmtunar en ekki til þess að sýna endanlegt sölu- eða kaupverð félags.<br />
                                        Keldan ehf. ber enga ábyrgð á röngum upplýsingum eða fjárhagstjóni sem kann að verða vegna verðmatsins.
                                    </p>}
                                />
                            }
                        />
                    </div>
                </div>
                <div className='shell'>
                    <CompanyTitle id={id} accessToken={accessToken} />
                    <div className='body'>
                        <div>
                            {displayBody()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Valuation;